'use client';

import { useTranslations } from 'next-intl';
import { type FC, useState } from 'react';
import { SliderField } from '@/app/components/shared/SliderField';
import { VoiceSelectionDialog } from '@/app/components/shared/VoiceSelectionDialog';
import { Switch } from '@/app/components/ui/switch';

interface LipSyncSettingsProps {
  voice?: string;
  speed: number;
  volume: number;
  onVoiceChange: (voiceId: string, isMyVoice: boolean) => void;
  onSpeedChange: (value: number) => void;
  onVolumeChange: (value: number) => void;
}

const SPEED_MIN = 0.5;
const SPEED_MAX = 2;
const SPEED_STEP = 0.1;

const VOLUME_MAX = 10;
const VOLUME_STEP = 1;

export const LipSyncSettings: FC<LipSyncSettingsProps> = ({
  voice,
  speed,
  volume,
  onVoiceChange,
  onSpeedChange,
  onVolumeChange,
}) => {
  const t = useTranslations();
  const [isVoiceDialogOpen, setIsVoiceDialogOpen] = useState(false);

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex flex-col gap-2">
        <span className="text-sm text-system-white">{t('tools.lipSync.settings.voice')}</span>
        <VoiceSelectionDialog
          open={isVoiceDialogOpen}
          onOpenChange={setIsVoiceDialogOpen}
          title={t('tools.lipSync.settings.voice')}
          value={voice}
          onConfirm={(nextVoiceId, nextVoice) =>
            onVoiceChange(nextVoiceId, Boolean(nextVoice && 'is_my' in nextVoice && nextVoice.is_my))
          }
        />
      </div>

      <SliderField
        label={t('tools.lipSync.settings.speed')}
        value={speed}
        min={SPEED_MIN}
        max={SPEED_MAX}
        step={SPEED_STEP}
        onChange={onSpeedChange}
        formatValue={(v) => `${v.toFixed(1)}x`}
      />

      <SliderField
        label={t('tools.lipSync.settings.volume')}
        value={volume || 1}
        min={1}
        max={VOLUME_MAX}
        step={VOLUME_STEP}
        onChange={onVolumeChange}
        showSlider={volume > 0}
        headerRight={
          <Switch
            className="h-7 w-12 p-0.5"
            checked={volume > 0}
            onCheckedChange={(checked) => onVolumeChange(checked ? 5 : 0)}
          />
        }
      />
    </div>
  );
};
